import React, { useEffect, useRef, useState } from 'react';

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  tabIndex?: number;
  className?: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
  }
}

const Turnstile: React.FC<TurnstileProps> = ({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'dark',
  size = 'normal',
  tabIndex,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load Turnstile script if not already loaded
    const loadTurnstile = () => {
      if (window.turnstile) {
        setIsLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        setIsLoaded(true);
      };
      
      script.onerror = () => {
        setError('Failed to load Turnstile script');
        onError?.('Failed to load Turnstile script');
      };

      document.head.appendChild(script);
    };

    loadTurnstile();
  }, [onError]);

  useEffect(() => {
    if (!isLoaded || !window.turnstile || !containerRef.current) {
      return;
    }

    // Remove existing widget if any
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (e) {
        // Ignore errors when removing
      }
      widgetIdRef.current = null;
    }

    try {
      // Render new widget
      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        tabindex: tabIndex,
        callback: (token: string) => {
          setError(null);
          onVerify(token);
        },
        'error-callback': (error: string) => {
          setError(error);
          onError?.(error);
        },
        'expired-callback': () => {
          onExpire?.();
        },
        'timeout-callback': () => {
          setError('Verification timed out');
          onError?.('Verification timed out');
        }
      });

      widgetIdRef.current = widgetId;
    } catch (err) {
      const errorMessage = 'Failed to render Turnstile widget';
      setError(errorMessage);
      onError?.(errorMessage);
      console.error('Turnstile render error:', err);
    }

    // Cleanup function
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [isLoaded, siteKey, theme, size, tabIndex, onVerify, onError, onExpire]);

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        setError(null);
      } catch (err) {
        console.error('Failed to reset Turnstile:', err);
      }
    }
  };

  // Expose reset method
  React.useImperativeHandle(React.forwardRef(() => containerRef), () => ({
    reset
  }));

  if (error) {
    return (
      <div className={`p-3 bg-red-900/50 border border-red-700 rounded-xl text-red-200 text-sm ${className}`}>
        <p>Security verification failed: {error}</p>
        <button
          onClick={reset}
          className="mt-2 text-xs text-red-300 hover:text-red-100 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className={`turnstile-container ${className}`}>
      <div ref={containerRef} />
      {!isLoaded && (
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
          <span className="ml-2 text-gray-400 text-sm">Loading security check...</span>
        </div>
      )}
    </div>
  );
};

export default Turnstile;
